// TEST-EXEMPT: Visual/animation-only component with no testable logic
import { useRef, useState, useEffect, type ReactNode } from 'react'
import { useWebGLSupport } from '@/hooks/use-webgl-support'

/**
 * LazyCanvas — mounts children only when the container scrolls into view.
 * Unmounts when it leaves the viewport to reclaim WebGL contexts.
 *
 * Why: Browsers limit active WebGL contexts to ~8-16. Pages with many
 * Three.js <Canvas> + LiquidChrome instances easily exceed this, causing
 * context loss (distorted/blank models). This wrapper keeps only visible
 * canvases alive.
 *
 * Also integrates WebGL degradation: on low-end mobile devices without
 * WebGL support, the fallback is rendered instead of 3D content.
 */
interface LazyCanvasProps {
  children: ReactNode
  /** Extra margin around viewport for pre-loading (default "200px") */
  rootMargin?: string
  /** CSS class for the outer container */
  className?: string
  /** Keep mounted after first visibility (default false = unmount on leave) */
  keepMounted?: boolean
  /** Placeholder to show while unmounted */
  fallback?: ReactNode
}

export default function LazyCanvas({
  children,
  rootMargin = '200px',
  className = '',
  keepMounted = false,
  fallback = null,
}: LazyCanvasProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const { shouldDegrade, ready } = useWebGLSupport()

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          setHasBeenVisible(true)
        } else if (!keepMounted) {
          setVisible(false)
        }
      },
      { rootMargin }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [rootMargin, keepMounted])

  // If device should degrade (no WebGL / low-tier mobile), always show fallback
  if (ready && shouldDegrade) {
    return (
      <div ref={ref} className={className} style={{ width: '100%', height: '100%' }}>
        {fallback}
      </div>
    )
  }

  const shouldRender = keepMounted ? hasBeenVisible : visible

  return (
    <div ref={ref} className={className} style={{ width: '100%', height: '100%' }}>
      {shouldRender ? children : fallback}
    </div>
  )
}
